import type { ClientByIdResponse, OrganizationDetailResponse } from "../types/clients-api.types";

type KycLinkJson = Record<string, unknown>;

/** Normalizes KYC fields from JSON (camelCase or snake_case). */
export function extractKycLinkFields(source: unknown): {
  activeKycLinkExpiresAt: string | null;
  activeKycLinkVerificationUrl: string | null;
  sumsub_applicant_id: string | null;
} {
  if (!source || typeof source !== "object") {
    return {
      activeKycLinkExpiresAt: null,
      activeKycLinkVerificationUrl: null,
      sumsub_applicant_id: null,
    };
  }
  const row = source as KycLinkJson;
  const exp = row.activeKycLinkExpiresAt ?? row.active_kyc_link_expires_at;
  const url = row.activeKycLinkVerificationUrl ?? row.active_kyc_link_verification_url;
  const sumsub = row.sumsub_applicant_id ?? row.sumsubApplicantId;
  const toStr = (v: unknown): string | null =>
    v == null || v === "" ? null : typeof v === "string" ? v : String(v);
  return {
    activeKycLinkExpiresAt: toStr(exp),
    activeKycLinkVerificationUrl: toStr(url),
    sumsub_applicant_id: toStr(sumsub),
  };
}

/**
 * Maps an OrganizationDetailResponse (admin tree endpoint) to ClientByIdResponse — the shape the KYC section
 * consumes. Fallback source of `useOrganizationKycProfile` when the client-profile endpoint is unavailable.
 */
export function mapOrganizationDetailToViewClient(org: OrganizationDetailResponse): ClientByIdResponse {
  const kycStatus = org.kyc_status ?? "PENDING";
  const kycLink = extractKycLinkFields(org);

  const parts = [org.name?.trim()].filter(Boolean);
  const isBusinessName = !parts[0]?.includes(" ");

  return {
    id: org.id,
    organization_id: null,
    // The admin/org-tree endpoint does not expose the per-entity identity id.
    identity_id: null,
    type: org.type ?? "business",
    company_name: isBusinessName ? org.name : null,
    first_name: !isBusinessName ? org.name : null,
    last_name: null,
    email: org.email,
    phone: org.phone ?? null,
    country: org.country,
    identification_type: null,
    identification_number: null,
    birthdate: null,
    incorporation_date: null,
    kyc_status: kycStatus,
    sumsub_applicant_id: kycLink.sumsub_applicant_id,
    kyc_verified_at: null,
    kyc_rejection_reason: null,
    kyc_documents: org.kyc_documents ?? null,
    accounts: [],
    activeKycLinkExpiresAt: kycLink.activeKycLinkExpiresAt,
    activeKycLinkVerificationUrl: kycLink.activeKycLinkVerificationUrl,
    created_at: org.created_at,
    updated_at: org.created_at,
    deleted_at: null,
  };
}
